import { createApi } from "@reduxjs/toolkit/query/react";
import { privateBaseQuery } from "@/api/base/private/privateBaseQuery";

export interface Certificate {
  id: number;
  name: string;
  description: string | null;
  image: string | null;
}

export interface UserCertificateListItem {
  id: number;
  name: string;
  certificate: {
    name: string;
    image: string | null;
  };
}

export interface UserCertificatesResponse {
  status: string;
  status_code: string;
  entities: UserCertificateListItem[];
}

export interface GetUserCertificatesParams {
  page?: number;
  orderBy?: "asc" | "desc";
  /** Optional server-side search param if backend supports it */
  query?: string;
}

export interface CertificatesLink {
  url: string | null;
  label: string;
  active: boolean;
}

export interface CertificatesEntities {
  current_page: number;
  data: Certificate[];
  first_page_url: string;
  from: number | null;
  last_page: number;
  last_page_url: string;
  links: CertificatesLink[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
}

export interface CertificateMetaItem {
  field: string;
  value: string;
}

export interface CertificateElementOptions {
  x: number;
  y: number;
  top: number;
  left: number;
  width: number;
  height: number;
  date_format?: string;
}

export interface CertificateElementStyles {
  top: string;
  left: string;
  align: string;
  color: string;
  width: string | null;
  height: string | null;
  valign: string;
  fontSize: string;
  fontFamily: string;
  fontWeight: string;

  /**
   * Optional fields used by backend certificate renderer (PDF/image generation).
   * They may be absent in older certificates.
   */
  fontStyle?: string;
  backgroundColor?: string | null;
  backgroundPadding?: number | string | null;
  textTransform?: string;
}

export interface CertificateElement {
  id: number;
  certificate_id: number;
  generated_id: string;
  type: string;
  variable: string | null;
  image: string | null;
  label: string | null;
  options: CertificateElementOptions;
  styles: CertificateElementStyles;
  is_active: number;
  is_native: number;
}

export interface CertificateElementPayload {
  id: number | null;
  certificate_id: number | null;
  generated_id: string | number;
  type: string;
  variable: string | null;
  image: string | null;
  label: string | null;
  name: string | null;
  options: Record<string, unknown>;
  styles: Record<string, unknown>;
  is_active: boolean | number;
  is_native: boolean | number;
}

export interface CertificateDetail extends Certificate {
  owner_id: number;
  meta: CertificateMetaItem[];
  serial_number: string | null;
  serial_counter: number | null;
  map: unknown | null;
  styles: {
    width: string;
    height: string;
  } | null;
  coefficient: number;
  elements: CertificateElement[];
}

export interface CertificateUpdatePayload {
  id: number;
  name: string;
  description: string | null;
  image: string | null;
  owner_id: number;
  serial_number: string | null;
  serial_counter: number | null;
  elements: CertificateElementPayload[];
  meta: CertificateMetaItem[];
  styles: {
    width: string;
    height: string;
  };
  coefficient: number;
  map: unknown | null;
}

export interface CertificateCreatePayload {
  id: number | null;
  owner_id: number | null;
  name: string;
  description: string | null;
  image: string | null;
  serial_number: string | null;
  serial_counter: number | null;
  elements: CertificateElementPayload[];
  meta: CertificateMetaItem[];
  styles: {
    width: string;
    height: string;
  };
  coefficient: number;
  map: unknown | null;
}

export interface CertificateDetailResponse {
  status: string;
  status_code: string;
  entity: CertificateDetail;
}

export interface CertificateCreateResponse {
  status: string;
  status_code: string;
  entity: {
    id: number;
    owner_id: number;
    name: string;
    description: string | null;
    meta: CertificateMetaItem[];
    serial_number: string | null;
    serial_counter: number | null;
    image: string | null;
  };
}

export interface CertificatesResponse {
  status: string;
  status_code: string;
  entities: CertificatesEntities;
}

export interface GetCertificatesParams {
  nullable?: 0 | 1;
  extended?: boolean;
  page?: number;
}

export interface IssueCertificatePayload {
  student_id: number;
}

export interface IssueCertificateResponse {
  status: string;
  status_code: string;
  entity: {
    id: number;
    name: string;
    certificate_id: number;
    certificate_number: string;
    issued_at: string;
    is_active: number;
  };
}

export interface RevokeCertificatePayload {
  student_id: number;
}

export interface RevokeCertificateResponse {
  status: string;
  status_code: string;
}

export const certificatesApi = createApi({
  reducerPath: "certificatesApi",
  baseQuery: privateBaseQuery(),
  tagTypes: ["Certificates", "UserCertificates"],
  endpoints: (builder) => ({
    getCertificates: builder.query<CertificatesEntities, GetCertificatesParams | void>({
      query: (params) => ({
        url: "/certificates",
        method: "GET",
        params: {
          nullable: 0,
          extended: false,
          page: 1,
          ...params,
        },
        headers: {
          "X-Requested-Fields": "id,name,description,image",
        },
      }),
      transformResponse: (response: CertificatesResponse) => response.entities,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((c) => ({
                type: "Certificates" as const,
                id: c.id,
              })),
              { type: "Certificates" as const, id: "LIST" },
            ]
          : [{ type: "Certificates" as const, id: "LIST" }],
    }),

    getUserCertificates: builder.query<UserCertificateListItem[], GetUserCertificatesParams | void>({
      query: (params) => ({
        url: "/user/certificates",
        method: "GET",
        params: {
          page: 1,
          orderBy: "desc",
          ...params,
        },
        headers: {
          "X-Requested-Fields": "id,name,certificate:name,certificate:image",
        },
      }),
      transformResponse: (response: UserCertificatesResponse) => response.entities,
      providesTags: (result) =>
        result
          ? [
              ...result.map((c) => ({
                type: "UserCertificates" as const,
                id: c.id,
              })),
              { type: "UserCertificates" as const, id: "LIST" },
            ]
          : [{ type: "UserCertificates" as const, id: "LIST" }],
    }),

    downloadUserCertificatePdf: builder.mutation<Blob, number>({
      query: (id) => ({
        url: `/user/certificates/${id}.pdf`,
        method: "POST",
        data: {},
        responseType: "blob",
      }),
    }),

    downloadCertificatePreview: builder.mutation<Blob, number>({
      query: (id) => ({
        url: `/certificates/${id}/preview.pdf`,
        method: "POST",
        data: {},
        responseType: "blob",
      }),
    }),

    getCertificateById: builder.query<CertificateDetail, number>({
      query: (id) => ({
        url: `/certificates/${id}`,
        method: "GET",
        headers: {
          "X-Requested-Fields":
            "id,owner_id,name,description,meta,serial_number,serial_counter,image,map,styles,coefficient,elements",
        },
      }),
      transformResponse: (response: CertificateDetailResponse) => response.entity,
      providesTags: (result, error, id) => [
        { type: "Certificates", id },
      ],
    }),
    updateCertificate: builder.mutation<CertificateDetail, CertificateUpdatePayload>({
      query: (payload) => ({
        url: `/certificates/${payload.id}`,
        method: "PUT",
        data: payload,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      transformResponse: (response: CertificateDetailResponse) => response.entity,
      invalidatesTags: (result, error, { id }) => [
        { type: "Certificates", id },
        { type: "Certificates", id: "LIST" },
      ],
    }),
    createCertificate: builder.mutation<
      CertificateCreateResponse["entity"],
      CertificateCreatePayload
    >({
      query: (payload) => ({
        url: "/certificates",
        method: "POST",
        data: payload,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      transformResponse: (response: CertificateCreateResponse) => response.entity,
      invalidatesTags: () => [
        { type: "Certificates", id: "LIST" },
      ],
    }),
    cloneCertificate: builder.mutation<CertificateDetail, number>({
      query: (id) => ({
        url: `/certificates/${id}/clone`,
        method: "POST",
        data: {},
        headers: {
          "Content-Type": "application/json",
        },
      }),
      transformResponse: (response: CertificateDetailResponse) => response.entity,
      invalidatesTags: () => [
        { type: "Certificates", id: "LIST" },
      ],
    }),
    
    issueCertificate: builder.mutation<
      IssueCertificateResponse,
      { assignmentId: number; studentId: number }
    >({
      query: ({ assignmentId, studentId }) => ({
        url: `/assignment/certificates/${assignmentId}/issue.json`,
        method: "POST",
        data: { student_id: studentId },
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: "UserCertificates", id: "LIST" },
      ],
    }),
    
    revokeCertificate: builder.mutation<
      RevokeCertificateResponse,
      { assignmentId: number; studentId: number }
    >({
      query: ({ assignmentId, studentId }) => ({
        url: `/assignment/certificates/${assignmentId}/revoke.json`,
        method: "POST",
        data: { student_id: studentId },
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: "UserCertificates", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetCertificatesQuery,
  useGetUserCertificatesQuery,
  useDownloadUserCertificatePdfMutation,
  useDownloadCertificatePreviewMutation,
  useGetCertificateByIdQuery,
  useLazyGetCertificateByIdQuery,
  useUpdateCertificateMutation,
  useCreateCertificateMutation,
  useCloneCertificateMutation,
  useIssueCertificateMutation,
  useRevokeCertificateMutation,
} = certificatesApi;